import { isEmpty, isEqual, isNil } from 'lodash'

import { haveLinksChanged } from './fieldLinks.helpers'

// The shared prompt's editable values, paired with the baseline RowEditorForm
// seeds them from. They are saved with the flow (see saveBitFlowForPlaybook), so
// an edit to any of them is an unsaved change even when no row moved.
export const PROMPT_BASELINE_FIELDS = [
  ['llm_model_id', 'loaded_llm_model_id'],
  ['system_prompt', 'loaded_system_prompt'],
  ['user_prompt', 'loaded_user_prompt'],
  ['max_response_tokens', 'loaded_max_response_tokens'],
]

// Which flow a form's loaded baseline was seeded from. A saved flow's version
// row is identified by id; the flow_uuid keeps two versions of the same
// configuration apart from two different configurations.
export const flowSnapshotKey = (flow) => {
  if (isNil(flow)) {
    return null
  }
  return `${flow.id ?? ''}:${flow.flow_uuid ?? ''}`
}

// Whether the form has been seeded from the flow on screen. Until it has, there
// is nothing meaningful to diff against — which is what stops the frame between
// a flow landing and its rows being seeded from reading as an edit.
const isBaselineSeeded = (values, flow) =>
  !isNil(flow) && values.loaded_flow_key === flowSnapshotKey(flow)

// Whether the editor's rows or gating conditions diverge from the configuration
// as it loaded.
export const hasUnsavedFieldChanges = (values = {}, flow) => {
  if (!isBaselineSeeded(values, flow)) {
    return false
  }

  return (
    !isEqual(values.prompt_fields, values.loaded_prompt_fields) ||
    haveLinksChanged(values.field_links ?? [], values.loaded_field_links ?? [])
  )
}

// Whether the shared prompt or the model it runs on has been edited. Separate
// from the rows because a discard puts these back from their own baseline.
export const hasUnsavedPromptChanges = (values = {}, flow) => {
  if (!isBaselineSeeded(values, flow)) {
    return false
  }

  return PROMPT_BASELINE_FIELDS.some(
    ([current, baseline]) => !isEqual(values[current], values[baseline])
  )
}

// Everything unsaved in the editor: the rows and conditions, the shared prompt
// and model above, plus option edits that were applied in a modal but never
// written (see PromptFieldRows — those are deliberately kept out of
// prompt_fields) and fields repointed at a different taxonomy than the saved
// configuration has.
export const hasUnsavedConfigChanges = (values = {}, flow, { switchedTaxonomyCount = 0 } = {}) =>
  hasUnsavedFieldChanges(values, flow) ||
  hasUnsavedPromptChanges(values, flow) ||
  !isEmpty(values.pending_taxonomies) ||
  switchedTaxonomyCount > 0
